import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useRecordConsent, useSealEnvelope, useSignEnvelope } from '@/api/hooks';
import { agreementContentPath } from '@/lib/agreement';
import { useDocumentContent } from '@/lib/useDocumentContent';
import { Button, ErrorText, HelpTip, Input, Label, PdfViewer, formatDate } from '@dataroom/shared';
import type { SigningEnvelope } from '@dataroom/shared';

/** The ESIGN/UETA consent the buyer gives by signing — recorded verbatim with
 * the consent step, so the audit trail holds exactly what was shown. */
export const AGREEMENT_CONSENT_TEXT =
  'By typing my name and selecting Sign, I confirm I have reviewed this agreement and I agree ' +
  'to use electronic records and signatures to sign it.';

const TITLES: Record<'nda' | 'nc', string> = {
  nda: 'Non-Disclosure Agreement',
  nc: 'Non-Circumvention Agreement',
};

interface AgreementSigningPanelProps {
  roomId: string;
  /** An admin-issued NDA/NC envelope assigned to this buyer. */
  envelope: SigningEnvelope;
}

/**
 * The buyer signs an admin-issued NDA/NC the way a person signs anything: they
 * READ it first. The agreement opens in the in-app, view-only viewer; below it
 * the buyer types their full name and selects Sign. Signing is impossible until
 * the document has actually loaded (pdf.js opened it, or the image rendered) —
 * the Sign button and the name field stay disabled before then, and an
 * agreement that cannot be shown cannot be signed.
 *
 * One click does the whole server walk (consent → sign → seal), resuming from
 * wherever the envelope already is, so the buyer never clicks through
 * bookkeeping steps; sealing advances the funnel and unlocks the CIM.
 */
export function AgreementSigningPanel({ roomId, envelope }: AgreementSigningPanelProps) {
  const consent = useRecordConsent(roomId);
  const sign = useSignEnvelope(roomId);
  const seal = useSealEnvelope(roomId);
  const [name, setName] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [viewFailed, setViewFailed] = useState(false);
  const nameId = useId();
  const hintId = useId();
  const titleId = useId();

  const title = envelope.envelopeType === 'nc' ? TITLES.nc : TITLES.nda;
  const sealed = envelope.status === 'sealed';
  const busy = consent.isPending || sign.isPending || seal.isPending;
  const actionError = consent.error ?? sign.error ?? seal.error;
  // Already signed (the seal step did not finish last time): the name is on
  // record, so only the finish remains — still behind a loaded document.
  const alreadySigned = envelope.status === 'signed';
  const nameOk = alreadySigned || name.trim().length >= 2;
  const canSign = loaded && nameOk && !busy;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSign) return;
    let status = envelope.status;
    try {
      if (status === 'created') {
        await consent.mutateAsync({ envelopeId: envelope.id, consentText: AGREEMENT_CONSENT_TEXT });
        status = 'consent_recorded';
      }
      if (status === 'consent_recorded') {
        // The typed name is the signature text of record.
        await sign.mutateAsync({ envelopeId: envelope.id, signatureName: name.trim() });
        status = 'signed';
      }
      if (status === 'signed') await seal.mutateAsync({ envelopeId: envelope.id });
    } catch {
      // Shown through the mutation's own error (ErrorText below); a retry
      // resumes from the step that failed.
    }
  };

  return (
    <section className="rounded-lg border" aria-labelledby={titleId}>
      <div className="flex items-center justify-between gap-4 border-b p-4">
        <div>
          <h3 id={titleId} className="text-sm font-medium">
            {title}
          </h3>
          <p className="text-xs text-muted-foreground">Issued {formatDate(envelope.createdAt)}</p>
        </div>
        <span className="shrink-0 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground">
          {sealed ? 'Signed' : 'Awaiting your signature'}
        </span>
      </div>

      {sealed ? (
        <p className="p-4 text-sm font-medium text-st-certified-fg">
          Completed — you signed this agreement and it is sealed.
        </p>
      ) : (
        <>
          <AgreementDocument
            contentUrl={agreementContentPath(roomId, envelope)}
            title={title}
            onReady={() => setLoaded(true)}
            onUnavailable={() => setViewFailed(true)}
          />
          <form className="space-y-3 border-t p-4" onSubmit={onSubmit} noValidate>
            <p className="text-sm text-muted-foreground">{AGREEMENT_CONSENT_TEXT}</p>
            {alreadySigned ? null : (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1">
                  <Label htmlFor={nameId}>Your full name</Label>
                  <HelpTip id="buyer.nda.sign" />
                </div>
                <Input
                  id={nameId}
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!loaded || busy}
                  aria-describedby={hintId}
                  className="max-w-sm"
                />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={!canSign} aria-describedby={hintId}>
                {busy ? 'Signing…' : alreadySigned ? 'Finish signing' : 'Sign'}
              </Button>
              <p id={hintId} className="text-xs text-muted-foreground" aria-live="polite">
                {viewFailed
                  ? 'This agreement can’t be shown, so it can’t be signed yet. Contact the deal team.'
                  : !loaded
                    ? 'The agreement must finish loading before you can sign.'
                    : nameOk
                      ? ''
                      : 'Type your full name to sign.'}
              </p>
            </div>
            <ErrorText error={actionError} />
          </form>
        </>
      )}
    </section>
  );
}

/** The agreement itself, view-only, inline above the signature. Reports when
 * it is actually on screen (`onReady`) or cannot be shown (`onUnavailable`). */
function AgreementDocument({
  contentUrl,
  title,
  onReady,
  onUnavailable,
}: {
  contentUrl: string;
  title: string;
  onReady: () => void;
  onUnavailable: () => void;
}) {
  const { content, error, loading } = useDocumentContent(
    contentUrl,
    'This agreement couldn’t be loaded.',
  );
  // An agreement is shown only as a PDF or an image; any other content is a failure.
  const failed = !!error || (!!content && content.kind !== 'pdf' && content.kind !== 'image');
  // Report a failure once it is known.
  const unavailableRef = useRef(onUnavailable);
  unavailableRef.current = onUnavailable;
  useEffect(() => {
    if (failed) unavailableRef.current();
  }, [failed]);

  return (
    <div
      className="relative h-[28rem] select-none bg-muted/30 sm:h-[36rem]"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {content?.kind === 'pdf' ? (
        <PdfViewer
          data={content.data}
          title={title}
          protect
          onLoad={onReady}
          onError={onUnavailable}
          className="h-full"
        />
      ) : (
        <div className="flex h-full items-center justify-center overflow-auto p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading agreement…</p>
          ) : error ? (
            <p role="alert" className="max-w-md text-center text-sm text-destructive">
              {error}
            </p>
          ) : content?.kind === 'image' ? (
            <img
              src={content.url}
              alt={title}
              draggable={false}
              onLoad={onReady}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <p role="alert" className="max-w-md text-center text-sm text-muted-foreground">
              This agreement’s file type can’t be shown in the viewer.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
